<template>
  <el-container class="exam-form-container">
    <el-main>
      <el-page-header content="录入试题" @back="handleBack" />
      <el-divider />

      <el-form ref="examForm" :model="form" :rules="rules" label-width="120px" size="medium">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="归属科目" prop="subjectId">
              <el-select v-model="form.subjectId" placeholder="请选择科目" style="width: 100%" filterable>
                <el-option
                  v-for="item in allSubject"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="大题型" prop="questionType">
              <el-select v-model="form.questionType" placeholder="请选择" style="width: 100%" @change="handleMainTypeChange">
                <el-option :value="1" label="单选题" />
                <el-option :value="2" label="多选题" />
                <el-option :value="3" label="填空题" />
                <el-option :value="4" label="问答题" />
                <el-option :value="5" label="组合题" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item :label="form.questionType === 5 ? '大题主干/阅读材料' : '试题题干'" prop="questionContent">
          <el-input
            v-model="form.questionContent"
            type="textarea"
            :rows="5"
            placeholder="请输入题目内容。支持 LaTeX 公式..."
            @input="lazyRenderFormula"
          />
        </el-form-item>

        <el-collapse-transition>
          <el-form-item v-if="form.questionContent" label="题干公式预览">
            <div class="formula-preview math-tex" v-html="form.questionContent" />
          </el-form-item>
        </el-collapse-transition>

        <div v-if="form.questionType >= 1 && form.questionType <= 4" class="option-config-box">
          <el-divider content-position="left">
            {{ (form.questionType === 1 || form.questionType === 2) ? '配置候选项' : '配置参考答案' }}
          </el-divider>

          <div v-if="form.questionType === 1 || form.questionType === 2" class="action-bar">
            <el-button type="primary" icon="el-icon-plus" size="small" @click="addFormAnswer">添加选项</el-button>
          </div>

          <el-table :data="form.options" border style="width: 100%">
            <el-table-column label="是否正确答案" width="120" align="center">
              <template slot-scope="scope">
                <el-checkbox
                  v-model="scope.row.correct"
                  :disabled="form.questionType === 3 || form.questionType === 4"
                  @change="checked => handleCheckAnswer(checked, scope.row.id)"
                />
              </template>
            </el-table-column>
            <el-table-column label="位置顺序" width="120" align="center">
              <template slot-scope="scope">
                <span>{{ scope.$index + 1 }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="(form.questionType === 1 || form.questionType === 2) ? '选项内容' : '标准参考答案'">
              <template slot-scope="scope">
                <el-input v-model="scope.row.options" size="small" :placeholder="(form.questionType === 1 || form.questionType === 2) ? '请输入选项内容，支持公式' : '请输入标准答案内容'" />
              </template>
            </el-table-column>
            <el-table-column label="答案解析(选填)">
              <template slot-scope="scope">
                <el-input v-model="scope.row.analysis" size="small" placeholder="针对此项的单独解析" />
              </template>
            </el-table-column>
            <el-table-column v-if="form.questionType === 1 || form.questionType === 2" label="操作" width="80" align="center">
              <template slot-scope="scope">
                <el-button type="danger" icon="el-icon-delete" circle size="mini" @click="delFormAnswer(scope.row.id)" />
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div v-if="form.questionType === 5" class="composite-box">
          <el-divider content-position="left">配置大题下的子问 / 子题集</el-divider>
          <div class="action-bar">
            <el-button type="success" icon="el-icon-circle-plus" size="small" @click="addSubQuestion">追加一个子问 (小题)</el-button>
          </div>

          <el-collapse v-model="activeCollapsePanels">
            <el-collapse-item
              v-for="(sub, index) in form.children"
              :key="sub.localId"
              :name="sub.localId"
            >
              <template slot="title">
                <div class="sub-title-container">
                  <span class="sub-title-text">
                    第 ({{ index + 1 }}) 问：【{{ getTypeName(sub.questionType) }}】
                  </span>
                  <el-button type="text" class="del-sub-btn" icon="el-icon-delete" @click.stop="delSubQuestion(index)">删除此问</el-button>
                </div>
              </template>

              <div class="sub-question-card">
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="子题型" label-width="70px">
                      <el-select v-model="sub.questionType" size="small" style="width: 100%" @change="handleSubQuestionTypeChange(sub)">
                        <el-option :value="1" label="单选题" />
                        <el-option :value="2" label="多选题" />
                        <el-option :value="3" label="填空题" />
                        <el-option :value="4" label="问答题" />
                      </el-select>
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-form-item label="子问具体题干" label-width="100px">
                  <el-input v-model="sub.questionContent" type="textarea" :rows="2" placeholder="请输入小题题干..." />
                </el-form-item>

                <div class="sub-answer-zone">
                  <div v-if="sub.questionType === 1 || sub.questionType === 2" class="action-bar-mini">
                    <el-button type="primary" plain size="mini" icon="el-icon-plus" @click="addSubAnswer(sub)">添加子问选项</el-button>
                  </div>
                  <el-table :data="sub.options" border size="mini" style="width: 100%">
                    <el-table-column label="答案" width="70" align="center">
                      <template slot-scope="scope">
                        <el-checkbox
                          v-model="scope.row.correct"
                          :disabled="sub.questionType === 3 || sub.questionType === 4"
                          @change="checked => handleCheckSubAnswer(checked, scope.row.id, sub)"
                        />
                      </template>
                    </el-table-column>
                    <el-table-column label="序号" width="70" align="center">
                      <template slot-scope="scope">
                        <span>{{ scope.$index + 1 }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column :label="(sub.questionType === 1 || sub.questionType === 2) ? '选项文本' : '标准参考答案'">
                      <template slot-scope="scope">
                        <el-input v-model="scope.row.options" size="mini" />
                      </template>
                    </el-table-column>
                    <el-table-column label="独立解析">
                      <template slot-scope="scope">
                        <el-input v-model="scope.row.analysis" size="mini" placeholder="选填" />
                      </template>
                    </el-table-column>
                    <el-table-column v-if="sub.questionType === 1 || sub.questionType === 2" label="操作" width="60" align="center">
                      <template slot-scope="scope">
                        <el-button type="text" class="danger-text" icon="el-icon-delete" @click="delSubAnswer(sub, scope.$index)" />
                      </template>
                    </el-table-column>
                  </el-table>
                </div>

                <el-form-item label="子问大意解析" label-width="100px" style="margin-top: 15px; margin-bottom: 0;">
                  <el-input v-model="sub.analysis" type="textarea" :rows="2" placeholder="选填（解析此步骤的详细思路）" />
                </el-form-item>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>

        <el-divider />
        <el-form-item :label="form.questionType === 5 ? '总题目解析' : '试题解析'" prop="analysis">
          <el-input v-model="form.analysis" type="textarea" :rows="4" placeholder="请输入官方标准整题解析步骤..." />
        </el-form-item>

        <el-form-item class="form-action-group">
          <el-button type="primary" size="medium" @click="submitForm">将试题保存至题库</el-button>
          <el-button size="medium" @click="backToList">返回列表</el-button>
        </el-form-item>
      </el-form>
    </el-main>
  </el-container>
</template>

<script>
import { addQuestion, getSubjectKV } from '@/api/exam'

export default {
  name: 'ExamQuestionAdd',
  data() {
    return {
      allSubject: [],
      activeCollapsePanels: [],
      form: {
        subjectId: '',
        questionType: 1,
        questionContent: '',
        analysis: '',
        extra: '',
        options: [],
        children: []
      },
      rules: {
        subjectId: [{ required: true, message: '请选择所属科目', trigger: 'change' }],
        questionType: [{ required: true, message: '请选择试题类型', trigger: 'change' }],
        questionContent: [{ required: true, message: '请输入题干内容', trigger: 'blur' }]
      },
      renderTimer: null,
      isChanged: false
    }
  },
  watch: {
    form: {
      handler() { this.isChanged = true },
      deep: true
    }
  },
  created() {
    document.title = '录入试题'
    this.getSubjects()
    this.initDefaultOptions()
  },
  beforeDestroy() {
    if (this.renderTimer) clearTimeout(this.renderTimer)
  },
  methods: {
    getSubjects() {
      getSubjectKV().then((resp) => {
        if (resp && resp.code === 0) this.allSubject = resp.data
      }).catch(err => console.error(err))
    },
    getTypeName(type) {
      const names = { 1: '单选题', 2: '多选题', 3: '填空题', 4: '问答题' }
      return names[type] || '未知题型'
    },
    initDefaultOptions() {
      if (this.form.questionType === 1 || this.form.questionType === 2) {
        this.form.options = [
          { id: `ans_${Date.now()}_1`, correct: false, options: '', analysis: '' },
          { id: `ans_${Date.now()}_2`, correct: false, options: '', analysis: '' }
        ]
      } else if (this.form.questionType === 3 || this.form.questionType === 4) {
        this.form.options = [
          { id: `ans_${Date.now()}_fixed`, correct: true, options: '', analysis: '' }
        ]
      } else {
        this.form.options = []
      }
    },
    handleMainTypeChange() {
      this.form.children = []
      this.initDefaultOptions()
    },
    handleSubQuestionTypeChange(sub) {
      if (sub.questionType === 1 || sub.questionType === 2) {
        sub.options = [
          { id: `s_opt_${Date.now()}_1`, correct: false, options: '', analysis: '' },
          { id: `s_opt_${Date.now()}_2`, correct: false, options: '', analysis: '' }
        ]
      } else if (sub.questionType === 3 || sub.questionType === 4) {
        sub.options = [
          { id: `s_opt_${Date.now()}_fixed`, correct: true, options: '', analysis: '' }
        ]
      } else {
        sub.options = []
      }
    },
    addFormAnswer() {
      if (this.form.questionType === 3 || this.form.questionType === 4) return
      this.form.options.push({
        id: `ans_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        correct: false,
        options: '',
        analysis: ''
      })
    },
    delFormAnswer(id) {
      this.form.options = this.form.options.filter(item => item.id !== id)
    },
    handleCheckAnswer(checked, id) {
      if (checked && this.form.questionType === 1) {
        this.form.options.forEach(item => { if (item.id !== id) item.correct = false })
      }
    },
    addSubQuestion() {
      const localId = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
      this.form.children.push({
        localId: localId,
        questionType: 1,
        questionContent: '',
        analysis: '',
        options: [
          { id: `s_opt_${localId}_1`, correct: false, options: '', analysis: '' },
          { id: `s_opt_${localId}_2`, correct: false, options: '', analysis: '' }
        ]
      })
      this.activeCollapsePanels.push(localId)
    },
    delSubQuestion(index) {
      this.form.children.splice(index, 1)
    },
    addSubAnswer(sub) {
      if (sub.questionType === 3 || sub.questionType === 4) return
      sub.options.push({
        id: `opt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        correct: false,
        options: '',
        analysis: ''
      })
    },
    delSubAnswer(sub, index) {
      sub.options.splice(index, 1)
    },
    handleCheckSubAnswer(checked, optId, sub) {
      if (checked && sub.questionType === 1) {
        sub.options.forEach(opt => { if (opt.id !== optId) opt.correct = false })
      }
    },
    lazyRenderFormula() {
      if (this.renderTimer) clearTimeout(this.renderTimer)
      this.renderTimer = setTimeout(() => {
        this.$nextTick(() => {
          if (window.MathJax && window.MathJax.typesetPromise) {
            window.MathJax.typesetPromise([document.querySelectorAll('.formula-preview')])
              .catch(err => console.error(err))
          }
        })
      }, 350)
    },
    formatPayload(formObj) {
      const convertOptions = (opts) => {
        return (opts || []).map((o, idx) => ({
          index: idx + 1,
          content: o.options,
          analysis: o.analysis || '',
          correct: !!o.correct
        }))
      }

      const convertQuestion = (q) => {
        return {
          questionType: q.questionType,
          subjectId: q.subjectId || null,
          questionContent: q.questionContent,
          analysis: q.analysis || '',
          extra: q.extra || '',
          options: q.questionType !== 5 ? convertOptions(q.options) : [],
          children: q.questionType === 5 ? (q.children || []).map(subQ => {
            subQ.subjectId = q.subjectId
            return convertQuestion(subQ)
          }) : []
        }
      }

      return convertQuestion(formObj)
    },
    submitForm() {
      this.$refs['examForm'].validate((valid) => {
        if (!valid) return

        if (this.form.questionType <= 4) {
          if (this.form.options.length === 0) return this.$message.error('请配置至少一个选项或答案！')
          if (this.form.options.some(a => !a.options.trim())) {
            const msg = (this.form.questionType === 1 || this.form.questionType === 2) ? '选项内容不能为空！' : '答案内容不能为空！'
            return this.$message.error(msg)
          }
          if ([1, 2].includes(this.form.questionType) && !this.form.options.some(a => a.correct)) {
            return this.$message.error('当前选择类题型未勾选正确答案！')
          }
        }

        if (this.form.questionType === 5) {
          if (this.form.children.length === 0) return this.$message.error('组合大题下请至少追加一个子问！')
          for (let i = 0; i < this.form.children.length; i++) {
            const sub = this.form.children[i]
            if (!sub.questionContent.trim()) return this.$message.error(`第 (${i + 1}) 问的子题干不能为空！`)
            if (sub.options.length === 0 || sub.options.some(a => !a.options.trim())) {
              return this.$message.error(`第 (${i + 1}) 问的选项或答案内容不能为空！`)
            }
            if ([1, 2].includes(sub.questionType) && !sub.options.some(a => a.correct)) {
              return this.$message.error(`第 (${i + 1}) 问小题未设置正确答案！`)
            }
          }
        }

        const payload = this.formatPayload(this.form)
        addQuestion(payload).then(resp => {
          if (resp && resp.code === 0) {
            this.$message.success('试题已成功保存至题库')
            this.isChanged = false
            this.resetForm()
          } else {
            this.$message.warning(resp.msg || '保存失败')
          }
        }).catch(err => console.error(err))
      })
    },
    resetForm() {
      if (this.$refs['examForm']) this.$refs['examForm'].resetFields()
      this.form = {
        subjectId: '', questionType: 1, questionContent: '', analysis: '', extra: '', options: [], children: []
      }
      this.activeCollapsePanels = []
      this.initDefaultOptions()
      this.$nextTick(() => { this.isChanged = false })
    },
    handleBack() {
      if (this.isChanged) {
        this.$confirm('数据尚未保存，确定要返回吗？', '提示', { type: 'warning' })
          .then(() => this.$router.push('/exam/question')).catch(() => {})
      } else {
        this.$router.push('/exam/question')
      }
    },
    backToList() { this.handleBack() }
  }
}
</script>

<style scoped>
.exam-form-container { background: #ffffff; border-radius: 8px; box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05); }
.formula-preview { padding: 12px 16px; background-color: #f8f9fa; border-left: 4px solid #409EFF; border-radius: 4px; min-height: 44px; font-size: 14px; line-height: 1.5; color: #303133; }
.option-config-box, .composite-box { margin-top: 10px; margin-bottom: 25px; }
.action-bar, .action-bar-mini { margin-bottom: 15px; }
.composite-box { background-color: #fcfcfc; padding: 20px; border-radius: 6px; border: 1px solid #e4e7ed; }
.sub-title-container { display: flex; justify-content: space-between; align-items: center; width: 100%; padding-right: 15px; }
.sub-title-text { font-weight: 600; color: #303133; }
.del-sub-btn { color: #f56c6c; }
.sub-question-card { padding: 20px; background: #ffffff; border: 1px solid #ebeef5; border-radius: 6px; }
.sub-answer-zone { margin-left: 20px; margin-top: 15px; margin-bottom: 15px; background: #fafafa; padding: 10px; border-radius: 4px; }
.form-action-group { margin-top: 40px; border-top: 1px solid #f1f1f1; padding-top: 20px; }
.danger-text { color: #F56C6C; }
::v-deep .el-table th { background-color: #f5f7fa !important; color: #606266; }
</style>
